import React, { forwardRef, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import LegacyButton from '@atlaskit/button';
import Button, { SplitButton } from '@atlaskit/button/new';
import LinkIcon from '@atlaskit/icon/core/migration/link';

import { token } from '@atlaskit/tokens';
import AKTooltip from '@atlaskit/tooltip';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { QuickAddItemToRender } from '../types.tsx';
import DropdownListButton from './ui/dropdown-list-button.tsx';

export type LinkButtonProps = {
	compactMode: boolean;
	actions: QuickAddItemToRender[];
	tooltipLabel: string;
	isQuickActionsListView?: boolean;
};

export type LinkButtonWrapperProps = LinkButtonProps;

const addLinkButtonSubject = 'AddLinkButton';
const addLinkButtonSubjectId = 'AddLink';

const LinkButton = forwardRef<HTMLButtonElement, LinkButtonProps>(
	({ compactMode, actions, tooltipLabel, isQuickActionsListView }: LinkButtonProps, ref) => {
		if (compactMode || (isQuickActionsListView && fg('quick_actions_menu_ga'))) {
			return (
				<DropdownListButton
					actions={actions}
					tooltip={tooltipLabel}
					isCompactMode={compactMode}
					isQuickActionsListView={isQuickActionsListView}
				/>
			);
		}

		const [{ id, onClick, label, tooltip: actionTooltip, testId }, ...rest] = actions;
		const buttonIcon = (
			<IconWrapper compactMode={compactMode}>
				<LinkIcon color="currentColor" spacing="spacious" LEGACY_size="medium" label="" />
			</IconWrapper>
		);
		const onClickWithAnalyticsEvent = (
			e: MouseEvent<HTMLElement>,
			analyticsEvent: UIAnalyticsEvent,
		) => {
			fireUIAnalytics(analyticsEvent, addLinkButtonSubjectId, { name: addLinkButtonSubject });
			onClick();
		};
		if (rest.length === 0) {
			return (
				<AKTooltip content={actionTooltip} position="bottom" key={id}>
					<LegacyButton
						aria-label={actionTooltip}
						spacing="default"
						iconBefore={buttonIcon}
						onClick={onClickWithAnalyticsEvent}
						testId={testId}
						interactionName="auto-generated.jira-issue-view-foundation.quick-add.link-button.button"
					>
						{label}
					</LegacyButton>
				</AKTooltip>
			);
		}

		return (
			<SplitButton>
				<AKTooltip content={actionTooltip} position="bottom" key={id}>
					<Button
						ref={ref}
						aria-label={actionTooltip}
						spacing="default"
						iconBefore={LinkIcon}
						onClick={onClickWithAnalyticsEvent}
						testId={testId}
						interactionName="auto-generated.jira-issue-view-foundation.quick-add.link-button.button1"
					>
						{label}
					</Button>
				</AKTooltip>
				<DropdownListButton actions={rest} placement="bottom-end" tooltip={tooltipLabel} />
			</SplitButton>
		);
	},
);

const LinkButtonWithWrapper = forwardRef<HTMLButtonElement, LinkButtonWrapperProps>(
	({ actions, ...restProps }: LinkButtonWrapperProps, ref) => {
		if (actions.length === 0) {
			return null;
		}

		if (restProps.isQuickActionsListView && fg('quick_actions_menu_ga')) {
			return <LinkButton actions={actions} ref={ref} {...restProps} />;
		}

		return (
			<ButtonWrapper data-testid="issue-view-foundation.quick-add.link-button.wrapper">
				<LinkButton actions={actions} ref={ref} {...restProps} />
			</ButtonWrapper>
		);
	},
);

export default LinkButtonWithWrapper;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.span<{ compactMode: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	padding: (props) => (props.compactMode ? `${gridSize / 2}px` : 0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginLeft: (props) => (props.compactMode ? 0 : '-4px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginRight: (props) => (props.compactMode ? 0 : '-2px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ButtonWrapper = styled.span({
	display: 'flex',
	marginRight: token('space.100', '8px'),
});
